import {
  aggregateFunctionResultType,
  isReportSubqueryAncestorAssociation,
  ReportAggregateFunction,
  ReportSubqueryAssociation,
  reportSubqueryAssociationToEntityType
} from '@cumu/shared';
import { controller, target } from '@github/catalyst';
import type { EditableListElement } from './editable-list';
import { dispatchChange } from './util';

@controller('report-subquery-aggregation')
class ReportSubqueryAggregationElement extends HTMLElement {
  @target declare associationSelect: HTMLSelectElement;
  @target declare aggregateSelect: HTMLSelectElement;
  @target declare aggregationTargetFormGroup: HTMLElement;

  updateAggregation() {
    const remote =
      this.aggregationTargetFormGroup.querySelector('remote-input')!;
    const url = new URL(remote.getAttribute('src')!, location.origin);
    const original_entity_type = url.searchParams.get('entity_type');
    const original_aggregate = url.searchParams.get('aggregate');

    const entity_type =
      reportSubqueryAssociationToEntityType[
        this.associationSelect.value as ReportSubqueryAssociation
      ];

    const entity_type_changed = original_entity_type !== entity_type;
    const aggregate_result_changed =
      aggregateFunctionResultType[
        original_aggregate as ReportAggregateFunction
      ] !==
      aggregateFunctionResultType[
        this.aggregateSelect.value as ReportAggregateFunction
      ];

    const propertyInput = this.aggregationTargetFormGroup.querySelector(
      'input[name^="definition/"]'
    ) as HTMLInputElement;

    if (entity_type_changed || aggregate_result_changed) {
      this.clearPropertyInput(propertyInput);
    }

    // reset filters and aggregate if entity type changed
    if (entity_type_changed) {
      this.aggregateSelect.value = 'count';
      this.closest('li')
        ?.querySelector<EditableListElement>('editable-list[name$="/filters"]')
        ?.removeAllListItems();
    }

    const aggregateGroup =
      this.aggregateSelect.closest<HTMLElement>('.form-group')!;
    if (isReportSubqueryAncestorAssociation(this.associationSelect.value)) {
      aggregateGroup.hidden = true;
      if (this.aggregateSelect.value !== 'max') {
        this.aggregateSelect.value = 'max';
        dispatchChange(this.aggregateSelect);
      }
    } else if (aggregateGroup.hidden) {
      aggregateGroup.hidden = false;
      if (this.aggregateSelect.value !== 'count') {
        this.aggregateSelect.value = 'count';
        dispatchChange(this.aggregateSelect);
      }
    }

    if (this.aggregateSelect.value === 'count') {
      // hide the property input
      this.aggregationTargetFormGroup.hidden = true;
      propertyInput.required = false;
    } else {
      // show the property input
      this.aggregationTargetFormGroup.hidden = false;
      propertyInput.required = true;
    }

    // update the property input's remote input
    this.updatePropertySuggestionSrc();
  }

  private clearPropertyInput(propertyInput: HTMLInputElement) {
    propertyInput.value = '';
    propertyInput.removeAttribute('value');
    propertyInput.removeAttribute('data-property');
    dispatchChange(propertyInput);
    propertyInput.previousElementSibling!.className =
      'color-fg-muted text-italic';
    propertyInput.previousElementSibling!.textContent = 'Select field...';
  }

  private updatePropertySuggestionSrc() {
    const entity_type =
      reportSubqueryAssociationToEntityType[
        this.associationSelect.value as ReportSubqueryAssociation
      ];

    const editableList = this.closest('li')?.querySelector<EditableListElement>(
      'editable-list[name$="filters"]'
    );

    const containers = [
      this.aggregationTargetFormGroup,
      editableList?.itemTemplate?.content
      // editableList.secondaryItemTemplate?.content,
      // editableList.tertiaryItemTemplate?.content
    ].filter(x => x !== undefined);

    for (const container of containers) {
      const remote = container.querySelector('remote-input')!;
      const url = new URL(remote.getAttribute('src')!, location.origin);
      url.searchParams.set('entity_type', entity_type);
      url.searchParams.set('association', this.associationSelect.value);
      url.searchParams.set('aggregate', this.aggregateSelect.value);
      remote.setAttribute('src', `${url.pathname}?${url.searchParams}`);
      const remoteFilterInput = remote.firstElementChild as HTMLInputElement;
      remoteFilterInput.value = '';
      dispatchChange(remoteFilterInput);
    }
  }
}

declare global {
  interface Window {
    ReportSubqueryAggregationElement: typeof ReportSubqueryAggregationElement;
  }
  interface HTMLElementTagNameMap {
    'report-subquery-aggregation': ReportSubqueryAggregationElement;
  }
}
